<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useConfirm } from '@/composables/useConfirm';
import { useToast } from '@/composables/useToast';
import { titleCase } from '@/utils/format';

// The adviser's review of the class's submitted grades before releasing
// them: one column per subject for a grading period, or every period plus
// each subject's final grade for the whole year. The period is picked on My
// Advisory Class. Grades are released one period at a time, once every
// subject has submitted it; the server checks that too.
const props = defineProps({
  // From My Advisory Class: grade_level, roomID, school_yearID, room name.
  room: { type: Object, default: null },
  // The grading period ('All' = whole year) and semester picked on the page.
  quarter: { type: String, default: null },
  semester: { type: String, default: null },
  // The page's grade submissions for that period (see AdviserRoomAssign).
  submissions: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const toast = useToast();
const { confirm } = useConfirm();

const WHOLE_YEAR = 'All';
const syType = computed(() => store.getters.getSyType);
const quarter = computed(() => props.quarter);
const semester = computed(() => props.semester);

const sexTab = shallowRef('All');
const students = shallowRef([]);
const subjects = shallowRef([]);
const periods = shallowRef([]);
const loading = shallowRef(false);
const submitting = shallowRef(false);
const wholeYear = computed(() => quarter.value === WHOLE_YEAR);

watch(open, (isOpen) => {
  if (!isOpen || !props.room) return;
  sexTab.value = 'All';
  search.value = '';
  load();
});
watch([quarter, semester], () => {
  if (open.value) load();
});

// Subjects on the class schedule that haven't submitted this period yet.
const missing = computed(() => props.submissions?.missing ?? []);
const alreadyReleased = computed(() => !!props.submissions?.released);
const releaseBlocked = computed(
  () => wholeYear.value || !students.value.length || missing.value.length > 0 || alreadyReleased.value,
);

const round = (n) => Math.round(Number(n));
const mean = (list) => (list.length ? list.reduce((a, b) => a + Number(b), 0) / list.length : null);

// Rows come one per student, subject and quarter; this pivots them into one
// row per student with a column per subject (and per quarter for the year).
function pivot(rows) {
  const byStudent = new Map();
  const subjectSet = new Set();
  const periodSet = new Set();
  rows.forEach((r) => {
    if (!byStudent.has(r.id)) {
      byStudent.set(r.id, { id: r.id, name: titleCase(r.name), lname: r.lname, sex: r.sex, lrnNo: r.lrnNo, grades: {} });
    }
    subjectSet.add(r.subject_title);
    periodSet.add(r.quarter);
    const s = byStudent.get(r.id);
    (s.grades[r.subject_title] ||= {})[r.quarter] = r.final_grade;
  });
  const subjectList = [...subjectSet];
  const list = [...byStudent.values()].map((s) => {
    const finals = {};
    subjectList.forEach((sub) => {
      const values = Object.values(s.grades[sub] || {}).filter((v) => v != null && v !== '');
      finals[sub] = values.length ? round(mean(values)) : null;
    });
    const subjectFinals = Object.values(finals).filter((v) => v != null);
    return { ...s, finals, average: subjectFinals.length ? round(mean(subjectFinals)) : null };
  });
  students.value = list.sort((a, b) => (a.lname || '').localeCompare(b.lname || ''));
  subjects.value = subjectList;
  periods.value = [...periodSet];
}

async function load() {
  if (!props.room || !quarter.value) return;
  loading.value = true;
  try {
    const { school_yearID, roomID } = props.room;
    const rows = await apiRequest(
      'get',
      `/enroll-student/getAllStudentsFinalGrade/${school_yearID}/${roomID}/${quarter.value}/${semester.value}`,
    );
    pivot(Array.isArray(rows) ? rows : []);
  } catch (e) {
    students.value = [];
    toast.error(errorMessage(e, 'Could not load the grades.'));
  } finally {
    loading.value = false;
  }
}

const counts = computed(() => ({
  All: students.value.length,
  Male: students.value.filter((s) => s.sex === 'Male').length,
  Female: students.value.filter((s) => s.sex === 'Female').length,
}));
const sexTabs = computed(() =>
  ['All', 'Male', 'Female'].map((id) => ({ id, name: id === 'All' ? 'All students' : id, count: counts.value[id] })),
);
// By name or LRN, within the chosen tab.
const search = shallowRef('');
const visible = computed(() => {
  const term = search.value.trim().toLowerCase();
  return students.value.filter(
    (s) =>
      (sexTab.value === 'All' || s.sex === sexTab.value) &&
      (!term || s.name.toLowerCase().includes(term) || String(s.lrnNo ?? '').includes(term)),
  );
});

// Wider as more subjects submit, so the grade columns aren't squeezed.
const columnCount = computed(() =>
  wholeYear.value ? subjects.value.length * (periods.value.length + 1) + 2 : subjects.value.length + 1,
);
const dialogSize = computed(() => (columnCount.value <= 5 ? 'xl' : columnCount.value <= 9 ? '2xl' : '3xl'));

// Same thresholds as the class record: 80+ passed, 75–79 warning, below 75 at risk.
const tone = (grade) => {
  if (grade == null || grade === '') return '';
  const g = Number(grade);
  return g >= 80 ? '' : g >= 75 ? 'fg-grade--warn' : 'fg-grade--risk';
};
const status = (grade) => {
  const g = Number(grade);
  if (grade == null) return null;
  return g >= 80 ? { text: 'Passed', variant: 'green' } : g >= 75 ? { text: 'Warning', variant: 'amber' } : { text: 'At risk', variant: 'red' };
};
const show = (v) => (v == null || v === '' ? '—' : v);

const description = computed(() =>
  [props.room?.grade_level, props.room?.name].filter(Boolean).join(' · '),
);
const periodLabel = computed(() => (wholeYear.value ? 'the whole year' : quarter.value));
const dialogTitle = computed(() => (wholeYear.value ? 'Whole-year grades' : `Release ${quarter.value} grades`));

function printGrades() {
  const { school_yearID, roomID, grade_level } = props.room;
  const route = syType.value == 0 ? 'getAllStudentsFinalGrade' : 'getAllStudentsFinalGradeV2';
  window.open(
    `${process.env.VUE_APP_SERVER}/pdf-generator/${route}/${school_yearID}/${roomID}/${quarter.value}/${semester.value}/${grade_level}`,
    '_blank',
  );
}
function printSF10() {
  window.open(
    `${process.env.VUE_APP_SERVER}/pdf-generator/getSchoolForm10/${store.getters.getFilterSelected}/${store.state.user.id}/${props.room.grade_level}`,
    '_blank',
  );
}

async function submitToParents() {
  const ok = await confirm({
    title: `Release ${periodLabel.value} grades to parents?`,
    description: "Parents will see these grades on their children's report cards and get a notification.",
    confirmText: 'Release grades',
  });
  if (!ok) return;
  submitting.value = true;
  try {
    const { roomID } = props.room;
    assertOk(
      await apiRequest('patch', `/rooms-section/updateParentToView/${roomID}`, {
        data: { filter: store.getters.getFilterSelected, quarter: quarter.value, semester: semester.value, roomID },
      }),
    );
    toast.success('Grades released to parents.');
    emit('saved');
    open.value = false;
  } catch (e) {
    toast.error(errorMessage(e, 'Could not release the grades.'));
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <UiDialog v-model="open" :title="dialogTitle" :description="description" :size="dialogSize" :persistent="false">
    <v-alert v-if="wholeYear" type="info" variant="tonal" density="compact" class="mb-3">
      Grades are released one grading period at a time. Pick a period on My Advisory Class to release it.
    </v-alert>
    <v-alert v-else-if="alreadyReleased" type="success" variant="tonal" density="compact" class="mb-3">
      {{ quarter }} grades are already released. Parents see them on the report card.
    </v-alert>
    <v-alert v-else-if="missing.length" type="warning" variant="tonal" density="compact" class="mb-3">
      Waiting for {{ missing.join(', ') }} to submit {{ quarter }} grades. You can release once every subject has.
    </v-alert>
    <div class="fg-toolbar">
      <v-text-field
        id="release-grades-search"
        v-model="search"
        placeholder="Search by name or LRN"
        prepend-inner-icon="mdi-magnify"
        density="compact"
        hide-details
        clearable
        class="fg-toolbar__search"
        aria-label="Search students"
      />
      <PageTabs v-model="sexTab" :items="sexTabs" class="fg-toolbar__tabs" />
    </div>

    <div class="fg-table">
      <div v-if="loading" class="fg-empty">Loading…</div>
      <div v-else-if="!students.length" class="fg-empty">
        No grades for {{ periodLabel }} yet. Grades appear here once subject teachers submit them.
      </div>
      <div v-else-if="!visible.length" class="fg-empty">No students match your search.</div>
      <table v-else>
        <thead>
          <tr>
            <th class="fg-name" :rowspan="wholeYear ? 2 : 1">Student</th>
            <template v-if="wholeYear">
              <th v-for="sub in subjects" :key="sub" :colspan="periods.length + 1" class="fg-group">{{ sub }}</th>
              <th rowspan="2" class="fg-num">Average</th>
              <th rowspan="2" class="fg-num">Status</th>
            </template>
            <template v-else>
              <th v-for="sub in subjects" :key="sub" class="fg-num">{{ sub }}</th>
              <th class="fg-num">Average</th>
            </template>
          </tr>
          <tr v-if="wholeYear">
            <template v-for="sub in subjects" :key="sub">
              <th v-for="p in periods" :key="p" class="fg-num fg-sub">{{ p.replace(' Quarter', '') }}</th>
              <th class="fg-num fg-sub">Final</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in visible" :key="s.id">
            <td class="fg-name">
              <span class="fg-name__text">{{ s.name }}</span>
              <span v-if="s.lrnNo" class="fg-name__lrn">{{ s.lrnNo }}</span>
            </td>
            <template v-if="wholeYear">
              <template v-for="sub in subjects" :key="sub">
                <td v-for="p in periods" :key="p" class="fg-num" :class="tone(s.grades[sub]?.[p])">
                  {{ show(s.grades[sub]?.[p]) }}
                </td>
                <td class="fg-num fg-final" :class="tone(s.finals[sub])">{{ show(s.finals[sub]) }}</td>
              </template>
              <td class="fg-num fg-final" :class="tone(s.average)">{{ show(s.average) }}</td>
              <td class="fg-num">
                <UiBadge v-if="status(s.average)" :variant="status(s.average).variant">{{ status(s.average).text }}</UiBadge>
              </td>
            </template>
            <template v-else>
              <td v-for="sub in subjects" :key="sub" class="fg-num" :class="tone(s.finals[sub])">
                {{ show(s.finals[sub]) }}
              </td>
              <td class="fg-num fg-final" :class="tone(s.average)">{{ show(s.average) }}</td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="fg-note">Grades below 75 are in red, 75–79 in amber.</p>

    <template #footer="{ close }">
      <div class="fg-footer-start">
        <v-btn variant="outlined" prepend-icon="mdi-printer-outline" :disabled="!students.length" @click="printGrades">
          Print grades
        </v-btn>
        <v-btn variant="outlined" prepend-icon="mdi-printer-outline" @click="printSF10">Print SF10</v-btn>
      </div>
      <v-btn variant="outlined" @click="close">Close</v-btn>
      <v-btn color="primary" :loading="submitting" :disabled="releaseBlocked" @click="submitToParents">
        Release to parents
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.fg-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.fg-toolbar__search {
  flex: 0 1 280px;
  min-width: 180px;
}
.fg-toolbar__tabs {
  margin-left: auto;
}
.fg-table {
  max-height: 58vh;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.fg-table table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.fg-table th,
.fg-table td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  white-space: nowrap;
}
.fg-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 500;
  color: var(--muted-foreground);
}
.fg-table thead tr + tr th {
  top: 35px;
}
.fg-table tbody tr:last-child td {
  border-bottom: 0;
}
.fg-name {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: 200px;
  text-align: left;
  box-shadow: 1px 0 0 var(--border);
}
.fg-table thead .fg-name {
  z-index: 3;
}
.fg-name__text {
  display: block;
  color: var(--foreground);
}
.fg-name__lrn {
  font-size: 12px;
  color: var(--muted-foreground);
}
.fg-group {
  text-align: center;
  border-left: 1px solid var(--border);
}
.fg-sub {
  font-size: 12px;
}
.fg-num {
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.fg-final {
  font-weight: 600;
}
.fg-grade--warn {
  color: #b45309;
}
.fg-grade--risk {
  color: #b91c1c;
}
html.dark .fg-grade--warn {
  color: #fcd34d;
}
html.dark .fg-grade--risk {
  color: #fca5a5;
}
.fg-empty {
  padding: 40px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.fg-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--muted-foreground);
}
.fg-footer-start {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-right: auto;
}
@media (max-width: 600px) {
  .fg-toolbar__tabs {
    margin-left: 0;
  }
}
</style>
